import { useState, useEffect } from 'react'
import { fetchWithAuth } from '../utils/api'
import { User } from '../types'

interface AssetDetailProps {
  assetId: number
  user: User
  assetTypes: Record<string, any>
  onClose: () => void
  onUpdate: () => void
}

interface Asset {
  id: number
  name: string
  type: string
  description: string | null
  site_name: string
  area_name: string | null
  created_by: number
  created_by_name: string
  created_by_fullname: string
  comment: string | null
  time_started: string
  time_ended: string | null
  position_type: 'point' | 'polygon'
  position_coords: [number, number] | [number, number][]
  status: 'active' | 'completed'
  duration: number
  created_at: string
  updated_at: string
}

function AssetDetail({ assetId, user, assetTypes, onClose, onUpdate }: AssetDetailProps) {
  const [asset, setAsset] = useState<Asset | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchAssetDetail()
  }, [assetId])

  const fetchAssetDetail = async () => {
    try {
      // Since we don't have a single asset endpoint, we'll get it from the list
      const response = await fetchWithAuth('/api/assets')
      if (!response.ok) {
        throw new Error('Failed to fetch asset details')
      }
      const data = await response.json()
      const foundAsset = data.assets.find((a: Asset) => a.id === assetId)
      if (!foundAsset) {
        throw new Error('Asset not found')
      }
      setAsset(foundAsset)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load asset')
    } finally {
      setLoading(false)
    }
  }

  const markAsCompleted = async () => {
    if (!asset) return

    try {
      const response = await fetchWithAuth(`/api/assets/${assetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...asset,
          time_ended: new Date().toISOString()
        })
      })

      if (!response.ok) {
        throw new Error('Failed to mark asset as completed')
      }

      onUpdate()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update asset')
    }
  }

  const markAsActive = async () => {
    if (!asset) return

    try {
      const response = await fetchWithAuth(`/api/assets/${assetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...asset,
          time_ended: null
        })
      })

      if (!response.ok) {
        throw new Error('Failed to mark asset as active')
      }

      onUpdate()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update asset')
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toISOString().slice(0, 19).replace('T', ' ')
  }

  const formatDuration = (days: number) => {
    if (days === 0) return 'Today'
    if (days === 1) return '1 day'
    return `${days} days`
  }

  const getAssetTypeInfo = (type: string) => {
    return assetTypes[type] || { name: type, icon: '📦', color: '#666' }
  }

  if (loading) {
    return (
      <div className="task-detail-overlay">
        <div className="task-detail-container">
          <div className="loading">Loading asset details...</div>
        </div>
      </div>
    )
  }

  if (error || !asset) {
    return (
      <div className="task-detail-overlay">
        <div className="task-detail-container">
          <div className="task-detail-header">
            <h3>Asset Details</h3>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="error">{error || 'Asset not found'}</div>
        </div>
      </div>
    )
  }

  const typeInfo = getAssetTypeInfo(asset.type)
  const canEdit = user.isAdmin || user.id === asset.created_by

  return (
    <div className="task-detail-overlay">
      <div className="task-detail-container">
        <div className="task-detail-header">
          <h3>📦 {asset.name}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="task-detail-content">
          <div className="detail-section">
            <div className="detail-badges">
              <span 
                className="priority-badge" 
                style={{ backgroundColor: typeInfo.color }}
              >
                {typeInfo.icon} {typeInfo.name}
              </span>
              <span 
                className="status-badge"
                style={{ 
                  backgroundColor: asset.status === 'active' ? '#27ae60' : '#95a5a6' 
                }}
              >
                {asset.status}
              </span>
            </div>
          </div>

          {asset.description && (
            <div className="detail-section">
              <h4>Description</h4>
              <p>{asset.description}</p>
            </div>
          )}

          <div className="detail-section">
            <h4>Location</h4>
            <div className="location-info">
              <p><strong>Site:</strong> 🏠 {asset.site_name}</p>
              {asset.area_name && (
                <p><strong>Area:</strong> 📍 {asset.area_name}</p>
              )}
              <p><strong>Position Type:</strong> {asset.position_type === 'point' ? '📍 Point' : '🟦 Area/Polygon'}</p>
              {asset.position_type === 'point' ? (
                <p><strong>Coordinates:</strong> {(asset.position_coords as [number, number])[0].toFixed(6)}, {(asset.position_coords as [number, number])[1].toFixed(6)}</p>
              ) : (
                <p><strong>Polygon:</strong> {(asset.position_coords as [number, number][]).length} points</p>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h4>Timeline</h4>
            <div className="timeline-info">
              <p><strong>Started:</strong> {formatDate(asset.time_started)}</p>
              {asset.time_ended ? (
                <p><strong>Ended:</strong> {formatDate(asset.time_ended)}</p>
              ) : (
                <p><strong>Status:</strong> Currently active</p>
              )}
              <p><strong>Duration:</strong> {formatDuration(asset.duration)}</p>
            </div>
          </div>

          <div className="detail-section">
            <h4>Details</h4>
            <p><strong>Created by:</strong> {asset.created_by_fullname || asset.created_by_name}</p>
            <p><strong>Created:</strong> {formatDate(asset.created_at)}</p>
            <p><strong>Last updated:</strong> {formatDate(asset.updated_at)}</p>
            {asset.comment && (
              <div>
                <strong>Comment:</strong>
                <p style={{ fontStyle: 'italic', marginTop: '0.5rem' }}>{asset.comment}</p>
              </div>
            )}
          </div>

          {canEdit && (
            <div className="detail-actions">
              {asset.status === 'active' ? (
                <button 
                  className="btn btn-warning"
                  onClick={markAsCompleted}
                >
                  ✓ Mark as Completed
                </button>
              ) : (
                <button 
                  className="btn btn-success"
                  onClick={markAsActive}
                >
                  🔄 Mark as Active
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AssetDetail